import React, { useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  X, Camera, ImageUp, Loader2, Sparkles, ShoppingCart,
  Download, Share2, RotateCcw, ShieldCheck, MapPin,
} from "lucide-react";
import http from "@/api";
import { buildBrandedImage } from "@/lib/brandShareImage";

const SIZES = ["small", "medium", "large"];

// يحوّل أي صورة (بما فيها HEIC من كاميرا الآيفون) إلى JPEG مضغوط ≤1600px قبل الرفع
async function compressPhoto(file, maxDim = 1600, quality = 0.85) {
  const bitmap = await new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new window.Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (err) => { URL.revokeObjectURL(url); reject(err); };
    img.src = url;
  });
  const scale = Math.min(1, maxDim / Math.max(bitmap.naturalWidth, bitmap.naturalHeight));
  const w = Math.round(bitmap.naturalWidth * scale);
  const h = Math.round(bitmap.naturalHeight * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  canvas.getContext("2d").drawImage(bitmap, 0, 0, w, h);
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/jpeg", quality));
  if (!blob) throw new Error("compress_failed");
  return new File([blob], "room.jpg", { type: "image/jpeg" });
}

const L = (lang) => ({
  title: lang === "ar" ? "شاهد المنتج في مكانك" : "See it in your space",
  privacy: lang === "ar"
    ? "لن يتم استخدام صورتك أو حفظها إلا بموافقتك — تُعالَج مؤقتاً فقط لإظهار المنتج."
    : "Your photo is never stored — it's processed only to preview the product.",
  capture: lang === "ar" ? "التقاط صورة" : "Take a photo",
  gallery: lang === "ar" ? "رفع من المعرض" : "Upload from gallery",
  tapHint: lang === "ar" ? "اضغط على المكان الذي تريد وضع المنتج فيه" : "Tap where you want the product placed",
  sizeLabel: lang === "ar" ? "حجم المنتج" : "Product size",
  small: lang === "ar" ? "صغير" : "Small",
  medium: lang === "ar" ? "متوسط" : "Medium",
  large: lang === "ar" ? "كبير" : "Large",
  generate: lang === "ar" ? "أظهر المنتج في مكانك" : "Show it in my space",
  generating: lang === "ar" ? "جارٍ دمج المنتج في صورتك…" : "Placing the product in your photo…",
  result: lang === "ar" ? "هكذا سيبدو في مكانك" : "Here's how it looks",
  addToCart: lang === "ar" ? "أعجبني الشكل — أضف للسلة" : "Love it — add to cart",
  save: lang === "ar" ? "حفظ الصورة" : "Save image",
  share: lang === "ar" ? "شارك التصميم" : "Share design",
  preparing: lang === "ar" ? "جارٍ التجهيز…" : "Preparing…",
  again: lang === "ar" ? "جرّب مكاناً/حجماً آخر" : "Try another spot/size",
  changePhoto: lang === "ar" ? "تغيير الصورة" : "Change photo",
  chooseSpot: lang === "ar" ? "حدّد المكان أولاً بالضغط على الصورة" : "Tap the photo to choose a spot first",
  err: lang === "ar" ? "تعذّر إنشاء المعاينة. حاول مرة أخرى." : "Couldn't create the preview. Please try again.",
  added: lang === "ar" ? "أُضيف إلى السلة ✓" : "Added to cart ✓",
});

export default function PlaceInSpaceModal({ open, onClose, product, lang, addToCart, formatPrice }) {
  const t = L(lang);
  const [photoUrl, setPhotoUrl] = useState(null);   // object URL for preview
  const [photoFile, setPhotoFile] = useState(null);
  const [marker, setMarker] = useState(null);       // {x,y} normalized 0..1
  const [size, setSize] = useState("medium");
  const [phase, setPhase] = useState("pick");        // pick | mark | loading | result
  const [resultUrl, setResultUrl] = useState(null);
  const [error, setError] = useState("");
  const [added, setAdded] = useState(false);
  const [branding, setBranding] = useState(false);
  const brandedRef = useRef(null);

  const camRef = useRef(null);
  const galRef = useRef(null);
  const imgRef = useRef(null);

  const reset = useCallback(() => {
    if (photoUrl) URL.revokeObjectURL(photoUrl);
    setPhotoUrl(null); setPhotoFile(null); setMarker(null);
    setSize("medium"); setPhase("pick"); setResultUrl(null); setError(""); setAdded(false);
    brandedRef.current = null;
  }, [photoUrl]);

  const close = () => { reset(); onClose(); };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (photoUrl) URL.revokeObjectURL(photoUrl);
    // ضغط/تحويل الصورة في المتصفح (يحوّل HEIC للآيفون إلى JPEG ويسرّع الرفع)
    const prepared = await compressPhoto(f).catch(() => f);
    setPhotoFile(prepared);
    setPhotoUrl(URL.createObjectURL(prepared));
    setMarker(null);
    setResultUrl(null);
    setError("");
    setPhase("mark");
  };

  const onTapImage = (e) => {
    const rect = imgRef.current.getBoundingClientRect();
    const cx = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
    const cy = (e.touches ? e.touches[0].clientY : e.clientY) - rect.top;
    setMarker({ x: Math.min(Math.max(cx / rect.width, 0), 1), y: Math.min(Math.max(cy / rect.height, 0), 1) });
  };

  const generate = async () => {
    if (!marker) { setError(t.chooseSpot); return; }
    setPhase("loading"); setError("");
    try {
      const fd = new FormData();
      fd.append("room_image", photoFile);
      fd.append("size", size);
      fd.append("place_x", String(marker.x));
      fd.append("place_y", String(marker.y));
      fd.append("lang", lang);
      const { data } = await http.post(`/products/${product.id}/place-in-space`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      brandedRef.current = null;
      setResultUrl(data.image);
      setPhase("result");
    } catch {
      setError(t.err);
      setPhase("mark");
    }
  };

  // Build (once) a branded version of the result: RARE GROUP logo + name + price + QR.
  const getBranded = async () => {
    if (brandedRef.current) return brandedRef.current;
    const price = formatPrice ? formatPrice(product.price_sar) : "";
    const url = `${window.location.origin}/p/${product.id}`;
    const name = lang === "ar" ? product.name_ar : product.name_en;
    const branded = await buildBrandedImage(resultUrl, { name, price, url, lang });
    brandedRef.current = branded;
    return branded;
  };

  const doSave = async () => {
    if (!resultUrl) return;
    setBranding(true);
    try {
      const img = await getBranded();
      const a = document.createElement("a");
      a.href = img;
      a.download = "rgpro-in-your-space.png";
      document.body.appendChild(a); a.click(); a.remove();
    } catch {
      const a = document.createElement("a");
      a.href = resultUrl; a.download = "rgpro-in-your-space.png";
      document.body.appendChild(a); a.click(); a.remove();
    } finally { setBranding(false); }
  };

  const doShare = async () => {
    if (!resultUrl) return;
    setBranding(true);
    try {
      const img = await getBranded();
      const blob = await (await fetch(img)).blob();
      const file = new File([blob], "rgpro-in-your-space.png", { type: "image/png" });
      const shareText = lang === "ar"
        ? `شاهد هذا المنتج من RARE GROUP 🌿 ${window.location.origin}/p/${product.id}`
        : `Check out this from RARE GROUP 🌿 ${window.location.origin}/p/${product.id}`;
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: "RARE GROUP", text: shareText });
      } else {
        const a = document.createElement("a");
        a.href = img; a.download = "rgpro-in-your-space.png";
        document.body.appendChild(a); a.click(); a.remove();
      }
    } catch { /* cancelled / unsupported */ }
    finally { setBranding(false); }
  };

  const doAdd = () => {
    addToCart(product, 1, null, []);
    setAdded(true);
    setTimeout(() => setAdded(false), 4000);
  };

  if (!open) return null;
  const name = lang === "ar" ? product.name_ar : product.name_en;

  return createPortal(
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={close} data-testid="place-in-space-overlay">
        <motion.div
          className="w-full sm:max-w-lg bg-white rounded-t-3xl sm:rounded-3xl overflow-hidden max-h-[92vh] flex flex-col"
          initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
          transition={{ type: "spring", damping: 26, stiffness: 260 }}
          onClick={(e) => e.stopPropagation()} dir={lang === "ar" ? "rtl" : "ltr"}
          data-testid="place-in-space-modal">

          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b" style={{ borderColor: "var(--border)" }}>
            <div className="flex items-center gap-2 font-bold">
              <Sparkles size={18} className="text-emerald-700" />
              {t.title}
            </div>
            <button onClick={close} className="w-9 h-9 rounded-full hover:bg-black/5 flex items-center justify-center"
                    data-testid="place-close-btn"><X size={18} /></button>
          </div>

          <div className="overflow-y-auto p-5 space-y-4">
            {/* Privacy notice — always visible */}
            <div className="flex items-start gap-2 text-xs bg-emerald-50 text-emerald-900 rounded-2xl px-3 py-2.5"
                 data-testid="place-privacy-note">
              <ShieldCheck size={16} className="shrink-0 mt-0.5" />
              <span>{t.privacy}</span>
            </div>

            {/* PHASE: pick source */}
            {phase === "pick" && (
              <div className="space-y-3" data-testid="place-pick-phase">
                <div className="grid grid-cols-2 gap-3">
                  <button onClick={() => camRef.current?.click()}
                          className="btn-outline flex-col !py-6 gap-2" data-testid="place-camera-btn">
                    <Camera size={26} /><span className="text-sm">{t.capture}</span>
                  </button>
                  <button onClick={() => galRef.current?.click()}
                          className="btn-outline flex-col !py-6 gap-2" data-testid="place-gallery-btn">
                    <ImageUp size={26} /><span className="text-sm">{t.gallery}</span>
                  </button>
                </div>
                <input ref={camRef} type="file" accept="image/*" capture="environment"
                       onChange={onFile} className="hidden" data-testid="place-camera-input" />
                <input ref={galRef} type="file" accept="image/*"
                       onChange={onFile} className="hidden" data-testid="place-gallery-input" />
              </div>
            )}

            {/* PHASE: mark spot + choose size */}
            {phase === "mark" && photoUrl && (
              <div className="space-y-4" data-testid="place-mark-phase">
                <div className="text-sm font-medium flex items-center gap-1.5">
                  <MapPin size={15} className="text-emerald-700" /> {t.tapHint}
                </div>
                <div className="relative rounded-2xl overflow-hidden select-none touch-none"
                     onClick={onTapImage} onTouchStart={onTapImage}>
                  <img ref={imgRef} src={photoUrl} alt="" className="w-full max-h-[46vh] object-contain bg-gray-100"
                       draggable={false} data-testid="place-room-image" />
                  {marker && (
                    <span className="absolute -translate-x-1/2 -translate-y-1/2 pointer-events-none"
                          style={{ left: `${marker.x * 100}%`, top: `${marker.y * 100}%` }}>
                      <span className="block w-6 h-6 rounded-full bg-emerald-600/90 ring-4 ring-white shadow-lg animate-pulse" />
                    </span>
                  )}
                </div>

                <div>
                  <div className="text-sm font-medium mb-2">{t.sizeLabel}</div>
                  <div className="grid grid-cols-3 gap-2">
                    {SIZES.map((s) => (
                      <button key={s} onClick={() => setSize(s)}
                              className={`py-2 rounded-xl border text-sm transition-all ${size === s ? "border-emerald-700 bg-emerald-50 font-semibold" : "border-transparent bg-black/5 hover:bg-black/10"}`}
                              data-testid={`place-size-${s}`}>{t[s]}</button>
                    ))}
                  </div>
                </div>

                {error && <div className="text-xs text-rose-600" data-testid="place-error">{error}</div>}

                <div className="flex gap-2">
                  <button onClick={() => setPhase("pick")} className="chip" data-testid="place-change-photo-btn">
                    {t.changePhoto}
                  </button>
                  <button onClick={generate} className="btn-primary flex-1 !py-3" data-testid="place-generate-btn">
                    <Sparkles size={18} /> {t.generate}
                  </button>
                </div>
              </div>
            )}

            {/* PHASE: loading */}
            {phase === "loading" && (
              <div className="py-14 flex flex-col items-center gap-4 text-center" data-testid="place-loading">
                <Loader2 size={40} className="animate-spin text-emerald-700" />
                <div className="text-sm opacity-80">{t.generating}</div>
              </div>
            )}

            {/* PHASE: result */}
            {phase === "result" && resultUrl && (
              <div className="space-y-4" data-testid="place-result-phase">
                <div className="text-sm font-semibold">{t.result}</div>
                <img src={resultUrl} alt={name}
                     className="w-full rounded-2xl bg-gray-100" data-testid="place-result-image" />

                {/* Branded share — turns the preview into free social advertising */}
                <button onClick={doShare} disabled={branding}
                        className="w-full flex items-center justify-center gap-2 rounded-2xl py-3 text-sm font-bold text-white bg-gradient-to-r from-emerald-700 to-[#000000] hover:opacity-95 transition-all shadow-md active:scale-[0.99] disabled:opacity-60"
                        data-testid="place-share-btn">
                  {branding ? <Loader2 size={18} className="animate-spin" /> : <Share2 size={18} />}
                  {branding ? t.preparing : t.share}
                </button>

                <div className="grid grid-cols-2 gap-2">
                  <button onClick={doSave} disabled={branding}
                          className="btn-outline !py-2.5 flex-col gap-1 !text-xs disabled:opacity-60" data-testid="place-save-btn">
                    <Download size={18} /> {t.save}
                  </button>
                  <button onClick={() => setPhase("mark")} className="btn-outline !py-2.5 flex-col gap-1 !text-xs" data-testid="place-retry-btn">
                    <RotateCcw size={18} /> {t.again}
                  </button>
                </div>
                <button onClick={doAdd} className="btn-primary w-full !py-3" data-testid="place-addtocart-btn">
                  <ShoppingCart size={18} /> {added ? t.added : t.addToCart}
                </button>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
}
